import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { inventoryAPI } from '../../services/inventory';

/**
 * #125114 (US-115332 mục E) — Thêm/sửa/xoá SĐT Zalo của NCC NGAY tại màn "Gửi đơn đặt hàng cho
 * NCC qua Zalo", không bắt vòng qua màn danh sách NCC.
 *
 * Ràng buộc đã chốt trong user story:
 * - Sửa INLINE ngay trong Card, KHÔNG chuyển màn, KHÔNG mở modal riêng (khác `SupplierFormModal`).
 * - Ghi ĐÚNG vào `Supplier.zalo_phone` qua đúng `PATCH /suppliers/:id` mà màn danh sách NCC dùng —
 *   CẤM tạo trường/API SĐT Zalo thứ hai, đổi ở màn nào cũng thấy ở màn kia.
 * - Trùng số với NCC khác đang active → CẢNH BÁO MỀM (server trả `warnings`), KHÔNG chặn lưu.
 * - Xoá số → modal xác nhận mạnh, nói rõ hậu quả nếu NCC đang có bộ cài tự động đang BẬT.
 * - Quyền giữ nguyên `InventoryManagerGuard` của `PATCH /suppliers/:id`, không nới lỏng ở FE.
 */

/** Chỉ để chặn sớm ở FE cho đỡ round-trip; server vẫn là nơi validate thật (`@Matches`). */
const VN_PHONE_REGEX = /^(?:\+?84|0)(?:3|5|7|8|9)\d{8}$/;
const normalize = (v) => String(v ?? '').replace(/[\s.\-()]/g, '').trim();

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');

export default function SupplierZaloPhoneEditor({ supplier, activeAutoConfigs = [], onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);

  // Đổi sang NCC khác thì đóng mọi thứ đang mở dở, không mang số của NCC trước sang NCC sau.
  useEffect(() => {
    setEditing(false);
    setValue('');
    setError(null);
    setConfirmDelete(false);
    setShowHistory(false);
    setHistory(null);
  }, [supplier?.id]);

  if (!supplier) return null;

  const openEdit = () => {
    setValue(supplier.zalo_phone || '');
    setError(null);
    setEditing(true);
  };

  const save = async (phone) => {
    try {
      setSaving(true);
      setError(null);
      const res = await inventoryAPI.updateSupplier(supplier.id, { zalo_phone: phone });
      const updated = res?.data || res;
      const warnings = updated?.warnings || [];
      warnings.forEach((w) => toast.warning(w));
      toast.success(phone ? 'Đã lưu SĐT Zalo' : 'Đã xoá SĐT Zalo của NCC');
      setEditing(false);
      setConfirmDelete(false);
      // Cập nhật NGAY trên màn — dùng được cho lần gửi kế tiếp, không cần tải lại trang (AC 22).
      onSaved?.({ ...supplier, zalo_phone: phone });
    } catch (err) {
      setError(err?.message || 'Lưu SĐT Zalo thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const phone = normalize(value);
    if (!phone) {
      setError('Vui lòng nhập SĐT Zalo, hoặc bấm Xoá nếu muốn bỏ số.');
      return;
    }
    if (!VN_PHONE_REGEX.test(phone)) {
      setError('SĐT không hợp lệ — cần 10 số bắt đầu 03/05/07/08/09 hoặc dạng +84...');
      return;
    }
    save(phone);
  };

  const openHistory = async () => {
    setShowHistory(true);
    if (history) return;
    try {
      const res = await inventoryAPI.getSupplierZaloPhoneHistory(supplier.id);
      setHistory(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error(err?.message || 'Không tải được lịch sử đổi SĐT Zalo');
      setHistory([]);
    }
  };

  const editForm = (
    <div className="mt-3" data-testid="zalo-phone-form">
      <Form.Label className="small text-muted mb-1">SĐT Zalo của NCC {supplier.name}</Form.Label>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <Form.Control
          type="tel"
          size="sm"
          style={{ maxWidth: 220 }}
          placeholder="VD: 0901234567"
          data-testid="zalo-phone-input"
          value={value}
          disabled={saving}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
        />
        <Button size="sm" variant="primary" data-testid="zalo-phone-save" disabled={saving} onClick={handleSave}>
          {saving ? 'Đang lưu…' : 'Lưu'}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          data-testid="zalo-phone-cancel"
          disabled={saving}
          onClick={() => { setEditing(false); setError(null); }}
        >
          Huỷ
        </Button>
      </div>
      {error && (
        <div className="text-danger small mt-1" data-testid="zalo-phone-error">{error}</div>
      )}
    </div>
  );

  return (
    <>
      {supplier.zalo_phone ? (
        <Alert variant="info" className="mt-3 mb-0 py-2">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span>
              Tin sẽ gửi tới SĐT Zalo:{' '}
              <strong data-testid="target-phone">{supplier.zalo_phone}</strong>
            </span>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <Button size="sm" variant="outline-primary" data-testid="btn-edit-zalo-phone" onClick={openEdit}>
                Sửa
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                data-testid="btn-delete-zalo-phone"
                onClick={() => setConfirmDelete(true)}
              >
                Xoá
              </Button>
              <Button size="sm" variant="link" className="p-0" data-testid="btn-zalo-phone-history" onClick={openHistory}>
                Lịch sử đổi số
              </Button>
            </div>
          </div>
          {editing && editForm}
        </Alert>
      ) : (
        <Alert variant="danger" className="mt-3 mb-0 py-2" data-testid="no-zalo-phone">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span>
              NCC <strong>{supplier.name}</strong> chưa có SĐT Zalo — không gửi được.
            </span>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <Button size="sm" variant="danger" data-testid="btn-add-zalo-phone" onClick={openEdit}>
                Thêm số Zalo
              </Button>
              <Button size="sm" variant="link" className="p-0" data-testid="btn-zalo-phone-history" onClick={openHistory}>
                Lịch sử đổi số
              </Button>
            </div>
          </div>
          {editing && editForm}
        </Alert>
      )}

      {/* AC 27 — xoá số là cảnh báo MẠNH, không xoá 1 chạm */}
      <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6 text-danger">Xoá SĐT Zalo của NCC?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Sẽ xoá số <strong>{supplier.zalo_phone}</strong> khỏi NCC <strong>{supplier.name}</strong>.
            Sau khi xoá, <b>không gửi được đơn đặt hàng qua Zalo</b> cho NCC này cho tới khi có số mới.
          </p>
          {activeAutoConfigs.length > 0 && (
            <Alert variant="warning" className="py-2 small mb-0" data-testid="delete-warn-auto-config">
              NCC này đang có <b>{activeAutoConfigs.length}</b> bộ cài tự động đang BẬT
              ({activeAutoConfigs.map((c) => c.name).join(', ')}). Xoá số này thì các bộ cài đó{' '}
              <b>vẫn tạo đơn nháp bình thường</b>, nhưng bước gửi Zalo thủ công cuối cùng sẽ báo lỗi
              “chưa có SĐT Zalo” cho tới khi bổ sung số mới.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDelete(false)} disabled={saving}>
            Huỷ
          </Button>
          <Button variant="danger" data-testid="btn-confirm-delete-zalo" disabled={saving} onClick={() => save(null)}>
            {saving ? 'Đang xoá…' : 'Xoá số Zalo'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* AC 26 — ghi vết, mở ra là biết ngay số nào của NCC nào, ai đổi, lúc nào */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h6">Lịch sử đổi SĐT Zalo — {supplier.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {history === null ? (
            <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
          ) : history.length === 0 ? (
            <div className="text-muted small">Chưa có lần đổi SĐT Zalo nào được ghi nhận.</div>
          ) : (
            <Table size="sm" bordered className="mb-0" data-testid="zalo-phone-history-table">
              <thead className="table-light">
                <tr><th>Thời gian</th><th>Người đổi</th><th>Số cũ</th><th>Số mới</th></tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="small">{fmtDateTime(h.created_at)}</td>
                    <td className="small">{h.user?.full_name || '—'}</td>
                    <td className="small">{h.old_value?.zalo_phone || <Badge bg="secondary">trống</Badge>}</td>
                    <td className="small">{h.new_value?.zalo_phone || <Badge bg="danger">đã xoá</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
